import {permiteRol} from '../../utils/permisosRol';
import { obtenerPerfilActual, obtenerEventosAccesibles, obtenerUsuariosDelAlcance, obtenerEmpresasDelAlcance } from '../../utils/eventAccess';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { collection, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { getDocs } from '../../utils/firestoreRead';
import './Empresas.css';


function crearSlugEmpresa(nombre) {
  return String(nombre || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'empresa';
}


export default function Empresas() {
  const navigate = useNavigate();
  const { profile, usuario } = useAuth() || {};

  const esOwner = permiteRol(profile||usuario,'empresas.gestionar');

  const [empresas, setEmpresas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [invitaciones, setInvitaciones] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nombre: '', color: '#2563eb', logo: '', activa: true });

  async function cargarDatos() {
    setCargando(true);
    setMensaje('');
    try {
      const acceso = await obtenerPerfilActual();
      if (!permiteRol(acceso.perfil,'empresas.gestionar')) {
        const [empresasScope, eventosScope, usuariosScope] = await Promise.all([
          obtenerEmpresasDelAlcance(acceso), obtenerEventosAccesibles('eventos', acceso), obtenerUsuariosDelAlcance(acceso),
        ]);
        setEmpresas(empresasScope); setEventos(eventosScope); setUsuarios(usuariosScope);
        setInvitaciones([]); setEntradas([]); setReservas([]);
      } else if(acceso.rol!=='owner'){
        const snap=await getDocs(collection(db,'empresas'));setEmpresas(snap.docs.map(d=>({...d.data(),id:d.id})));setEventos([]);setUsuarios([]);setInvitaciones([]);setEntradas([]);setReservas([]);
      } else {
        const names = ['empresas', 'eventos', 'usuarios', 'invitaciones', 'entradas', 'reservas'];
        const snapshots = await Promise.all(names.map((name) => getDocs(collection(db, name))));
        const rows = snapshots.map((snap) => snap.docs.map((d) => ({ ...d.data(), id: d.id })));
        setEmpresas(rows[0]); setEventos(rows[1]); setUsuarios(rows[2]);
        setInvitaciones(rows[3]); setEntradas(rows[4]); setReservas(rows[5]);
      }
    } catch (error) {
      console.error('Error cargando empresas:', error);
      setMensaje('No se pudo cargar el módulo de empresas. Revisa permisos de Firestore.');
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarDatos();
  }, []);

  const empresasConsolidadas = useMemo(() => {
    const mapa = new Map();

    empresas.forEach((e) => {
      if (!e?.id) return;
      mapa.set(e.id, {
        id: e.id,
        nombre: String(e.nombre || e.id).toUpperCase(),
        activa: e.activa !== false,
        color: e.color || '#2563eb',
        logo: e.logo || '',
        origen: 'empresas',
      });
    });

    return Array.from(mapa.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [empresas]);

  const empresasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return empresasConsolidadas;
    return empresasConsolidadas.filter((e) => e.nombre.toLowerCase().includes(q) || e.id.toLowerCase().includes(q));
  }, [empresasConsolidadas, busqueda]);

  function contarPorEmpresa(lista, empresa) {
    return lista.filter((item) => {
      const itemEmpresaId = item?.empresaId;
      return itemEmpresaId === empresa.id;
    }).length;
  }

  function metricasEmpresa(empresa) {
    return {
      eventos: esOwner&&profile?.rol!=='owner'?'—':contarPorEmpresa(eventos, empresa),
      usuarios: esOwner&&profile?.rol!=='owner'?'—':contarPorEmpresa(usuarios, empresa),
      invitaciones: profile?.rol==='owner' ? contarPorEmpresa(invitaciones, empresa) : '—',
      entradas: profile?.rol==='owner' ? contarPorEmpresa(entradas, empresa) : '—',
      reservas: profile?.rol==='owner' ? contarPorEmpresa(reservas, empresa) : '—',
    };
  }

  function abrirCrear() {
    setForm({ nombre: '', color: '#2563eb', logo: '', activa: true });
    setModal({ tipo: 'crear' });
  }

  function abrirEditar(empresa) {
    setForm({
      nombre: empresa.nombre || '',
      color: empresa.color || '#2563eb',
      logo: empresa.logo || '',
      activa: empresa.activa !== false,
    });
    setModal({ tipo: 'editar', empresa });
  }

  async function guardarEmpresa(e) {
    e.preventDefault();
    if (!esOwner) {
      setMensaje('Solo el owner puede crear o editar empresas.');
      return;
    }

    const nombre = form.nombre.trim().toUpperCase();
    if (!nombre) {
      setMensaje('Escribe el nombre de la empresa.');
      return;
    }

    const id = modal?.tipo === 'editar' ? modal.empresa.id : crearSlugEmpresa(nombre);

    try {
      await setDoc(doc(db, 'empresas', id), {
        nombre,
        activa: form.activa !== false,
        color: form.color || '#2563eb',
        logo: form.logo || '',
        updatedAt: serverTimestamp(),
        ...(modal?.tipo === 'crear' ? { createdAt: serverTimestamp() } : {}),
      }, { merge: true });

      setModal(null);
      setMensaje(modal?.tipo === 'crear' ? 'Empresa creada correctamente.' : 'Empresa actualizada correctamente.');
      await cargarDatos();
    } catch (error) {
      console.error('Error guardando empresa:', error);
      setMensaje('No se pudo guardar la empresa. Revisa las reglas de Firestore.');
    }
  }

  async function cambiarEstado(empresa) {
    if (!esOwner) {
      setMensaje('Solo el owner puede activar o desactivar empresas.');
      return;
    }
    try {
      await updateDoc(doc(db, 'empresas', empresa.id), {
        activa: empresa.activa === false,
        updatedAt: serverTimestamp(),
      });
      await cargarDatos();
    } catch (error) {
      console.error('Error cambiando estado de empresa:', error);
      setMensaje('No se pudo cambiar el estado de la empresa.');
    }
  }


  async function eliminarEmpresa(empresa) {
    if (!esOwner) {
      setMensaje('Solo el owner puede eliminar empresas.');
      return;
    }

    if (empresa.origen !== 'empresas') {
      setMensaje('Esta empresa no se puede eliminar porque es base o fue detectada por datos existentes.');
      return;
    }

    const m = metricasEmpresa(empresa);
    const totalVinculados = m.eventos + m.usuarios + m.invitaciones + m.entradas + m.reservas;

    if (totalVinculados > 0) {
      setMensaje(`No se puede eliminar ${empresa.nombre}. Tiene registros vinculados: ${m.eventos} evento(s), ${m.usuarios} usuario(s), ${m.invitaciones} invitacion(es), ${m.entradas} entrada(s) y ${m.reservas} reserva(s).`);
      return;
    }

    const confirmar = window.confirm(`¿Eliminar definitivamente la empresa ${empresa.nombre}? Esta acción no se puede deshacer.`);
    if (!confirmar) return;

    try {
      await deleteDoc(doc(db, 'empresas', empresa.id));
      setMensaje('Empresa eliminada correctamente.');
      await cargarDatos();
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      setMensaje('No se pudo eliminar la empresa. Revisa las reglas de Firestore.');
    }
  }

  const totalEventos = eventos.length;
  const activas = empresasConsolidadas.filter((e) => e.activa !== false).length;
  const inactivas = empresasConsolidadas.length - activas;

  return (
    <div className="emp-page">
      <div className="emp-shell">
        <header className="emp-hero">
          <div className="emp-hero-content">
            <span className="emp-eyebrow">Panel multiempresa</span>
            <h1>Empresas</h1>
            <p>Administra clientes, eventos, personal, invitaciones, entradas y reservas desde un solo lugar.</p>
          </div>
          <div className="emp-header-actions">
            <button className="emp-btn emp-btn-light" onClick={() => navigate('/eventos')}>Ver eventos</button>
            {esOwner && <button className="emp-btn emp-btn-primary" onClick={abrirCrear}>+ Crear empresa</button>}
          </div>
        </header>

        <section className="emp-kpi-grid">
          <Kpi label="Empresas" value={empresasConsolidadas.length} note="Total registradas" />
          <Kpi label="Activas" value={activas} note={`${inactivas} inactiva(s)`} />
          <Kpi label="Eventos" value={totalEventos} note="Eventos creados" />
          <Kpi label="Usuarios" value={usuarios.length} note="Personal vinculado" />
        </section>

        <section className="emp-toolbar-card">
          <div className="emp-search-box">
            <span>⌕</span>
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por empresa o identificador..."
            />
          </div>
          <button className="emp-btn emp-btn-light" onClick={cargarDatos} disabled={cargando}>
            {cargando ? 'Cargando...' : 'Actualizar'}
          </button>
        </section>

        {mensaje && <div className="emp-message">{mensaje}</div>}

        <section className="emp-panel">
          <div className="emp-panel-header">
            <div>
              <h2>Listado de empresas</h2>
              <p>{empresasFiltradas.length} resultado(s)</p>
            </div>
            <span className="emp-result-badge">{cargando ? 'Sincronizando' : 'Actualizado'}</span>
          </div>

          <div className="emp-desktop-table">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Eventos</th>
                  <th>Usuarios</th>
                  <th>Invitaciones</th>
                  <th>Entradas</th>
                  <th>Reservas</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresasFiltradas.map((empresa) => {
                  const m = metricasEmpresa(empresa);
                  return (
                    <tr key={empresa.id}>
                      <td>
                        <EmpresaIdentity empresa={empresa} />
                      </td>
                      <td>{m.eventos}</td>
                      <td>{m.usuarios}</td>
                      <td>{m.invitaciones}</td>
                      <td>{m.entradas}</td>
                      <td>{m.reservas}</td>
                      <td><EstadoBadge activa={empresa.activa !== false} /></td>
                      <td>
                        <EmpresaActions
                          empresa={empresa}
                          esOwner={esOwner}
                          onEventos={() => navigate(`/eventos?empresa=${empresa.id}`)}
                          onPersonal={() => navigate(`/dashboard/gestion-personal?empresa=${empresa.id}`)}
                          onEditar={() => abrirEditar(empresa)}
                          onEstado={() => cambiarEstado(empresa)}
                          onEliminar={() => eliminarEmpresa(empresa)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {!empresasFiltradas.length && (
                  <tr><td className="emp-empty" colSpan="8">No hay empresas para mostrar.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="emp-mobile-list">
            {empresasFiltradas.map((empresa) => {
              const m = metricasEmpresa(empresa);
              return (
                <article className="emp-company-card" key={empresa.id}>
                  <div className="emp-card-top">
                    <EmpresaIdentity empresa={empresa} />
                    <EstadoBadge activa={empresa.activa !== false} />
                  </div>

                  <div className="emp-card-stats">
                    <MiniStat label="Eventos" value={m.eventos} />
                    <MiniStat label="Usuarios" value={m.usuarios} />
                    <MiniStat label="Invitaciones" value={m.invitaciones} />
                    <MiniStat label="Entradas" value={m.entradas} />
                    <MiniStat label="Reservas" value={m.reservas} />
                  </div>

                  <EmpresaActions
                    empresa={empresa}
                    esOwner={esOwner}
                    onEventos={() => navigate(`/eventos?empresa=${empresa.id}`)}
                    onPersonal={() => navigate(`/dashboard/gestion-personal?empresa=${empresa.id}`)}
                    onEditar={() => abrirEditar(empresa)}
                    onEstado={() => cambiarEstado(empresa)}
                    onEliminar={() => eliminarEmpresa(empresa)}
                  />
                </article>
              );
            })}
            {!empresasFiltradas.length && <div className="emp-empty emp-empty-card">No hay empresas para mostrar.</div>}
          </div>
        </section>
      </div>

      {modal && (
        <div className="emp-modal-overlay" onClick={() => setModal(null)}>
          <form className="emp-modal" onSubmit={guardarEmpresa} onClick={(e) => e.stopPropagation()}>
            <div className="emp-modal-head">
              <div>
                <h2>{modal.tipo === 'crear' ? 'Crear empresa' : 'Editar empresa'}</h2>
                <p>Esta empresa separará eventos, personal, invitaciones, entradas y reservas.</p>
              </div>
              <button type="button" className="emp-icon-close" onClick={() => setModal(null)}>×</button>
            </div>

            <label>Nombre de empresa</label>
            <input
              className="emp-input"
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: VINCENT"
            />

            <label>Color identificador</label>
            <div className="emp-color-row">
              <input
                className="emp-color-input"
                type="color"
                value={/^#[0-9A-F]{6}$/i.test(form.color) ? form.color : '#2563eb'}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                aria-label="Color identificador"
              />
              <input
                className="emp-input"
                value={form.color}
                onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                placeholder="#2563eb"
              />
            </div>

            <label>Logo URL opcional</label>
            <input
              className="emp-input"
              value={form.logo}
              onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
              placeholder="https://..."
            />

            <label className="emp-check-row">
              <input
                type="checkbox"
                checked={form.activa !== false}
                onChange={(e) => setForm((f) => ({ ...f, activa: e.target.checked }))}
              />
              Empresa activa
            </label>

            <div className="emp-modal-actions">
              <button type="button" className="emp-btn emp-btn-light" onClick={() => setModal(null)}>Cancelar</button>
              <button type="submit" className="emp-btn emp-btn-primary">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="emp-kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  );
}

function EmpresaIdentity({ empresa }) {
  return (
    <div className="emp-company-cell">
      <div className="emp-logo" style={{ background: empresa.color || '#2563eb' }}>
        {empresa.logo ? <img src={empresa.logo} alt="logo" /> : empresa.nombre.slice(0, 2)}
      </div>
      <div className="emp-company-info">
        <strong>{empresa.nombre}</strong>
        <small>{empresa.id}</small>
        {empresa.origen === 'detectada' && <span className="emp-warn-badge">Detectada por datos</span>}
      </div>
    </div>
  );
}

function EstadoBadge({ activa }) {
  return <span className={activa ? 'emp-status emp-status-active' : 'emp-status emp-status-inactive'}>{activa ? 'Activa' : 'Inactiva'}</span>;
}

function MiniStat({ label, value }) {
  return (
    <div className="emp-mini-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function EmpresaActions({ empresa, esOwner, onEventos, onPersonal, onEditar, onEstado, onEliminar }) {
  return (
    <div className="emp-actions">
      <button className="emp-action-btn" onClick={onEventos}>Eventos</button>
      {esOwner && <button className="emp-action-btn" onClick={onPersonal}>Personal</button>}
      {esOwner && <button className="emp-action-btn" onClick={onEditar}>Editar</button>}
      {esOwner && empresa.origen !== 'detectada' && (
        <button className={empresa.activa === false ? 'emp-enable-btn' : 'emp-danger-btn'} onClick={onEstado}>
          {empresa.activa === false ? 'Activar' : 'Desactivar'}
        </button>
      )}
      {esOwner && empresa.origen === 'empresas' && (
        <button className="emp-danger-btn" onClick={onEliminar}>
          Eliminar
        </button>
      )}
    </div>
  );
}
